import React, { useState } from 'react';
import type { CrudAddModalProps } from '../crud/CrudTable';
import useComputedSlug from '../../hooks/useComputedSlug';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import { Button, Container, Group, Loader, Select, Stack, TextInput, Textarea } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { modals } from '@mantine/modals';
import { useEventService } from '../../services/EventService';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import type { CategoryListFragment, EventListFragment, EventSingleFragment } from '../../graphql/graphql';
import useLoadingEffect from '@dr-pam/common-components/Hooks/useLoadingEffect';
import type { CategorySortableTreeItem } from '../../services/CategoryService';
import { useCategoryService } from '../../services/CategoryService';
import z from 'zod';

export const validationSchema = z.object({
	title: z.string().nonempty().max(250),
	slug: z.string().nonempty().max(250),
	description: z.string().nonempty(),
});

export type AddEventModalProps = CrudAddModalProps<EventListFragment, EventSingleFragment> & {
	categoryId?: string;
};

export default function AddEventModal(props: AddEventModalProps) {
	const { modalId, onCancel, onCreated, categoryId } = props;

	const { addLoader, removeLoader, isLoading } = useLoadTracker();

	const eventService = useEventService();
	const categoryService = useCategoryService();

	const [categories, setCategories] = useState<CategorySortableTreeItem<CategoryListFragment>[]>([]);

	const isLoadingCategories = useLoadingEffect(async () => {
		try {
			const response = await categoryService.getAllInFlattenedTree().response;
			setCategories(response);
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to load categories');
		}
	}, [categoryService, categoryId]);

	const form = useForm<AddEventForm>({
		initialValues: {
			title: '',
			slug: '',
			description: '',
			categoryId: categoryId ?? '',
		},
		validate: schemaResolver(validationSchema),
	});

	const handleSubmit = async (values: AddEventForm) => {
		const loader = addLoader();

		try {
			const createdEvent = await eventService.create({
				title: values.title,
				slug: values.slug,
				description: values.description,
				...((categoryId || values.categoryId) && {
					eventCategories: {
						create: [
							{
								category: {
									connect: {
										id: categoryId || values.categoryId,
									},
								},
							},
						],
					},
				}),
			});
			onCreated(createdEvent);
			NotificationUtils.showSuccess('Successfully created event', createdEvent.title);
			modals.close(modalId);
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to create event');
			removeLoader(loader);
		}
	};

	const handleCancel = () => {
		onCancel?.();
		modals.close(modalId);
	};

	const slugComputedFromProps = useComputedSlug(form, 'slug', 'title');

	const categoryData = (categories ?? []).map((category) => ({
		value: category.item.id,
		label: category.getName(),
	}));

	return (
		<Container>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack>
					<TextInput
						{...slugComputedFromProps}
						label="Title"
						description="The title of the event."
						disabled={isLoading}
						withAsterisk
					/>
					<Textarea
						{...form.getInputProps('description')}
						label="Description"
						description="The description of the event. Include Zoom links."
						disabled={isLoading}
						withAsterisk
						rows={5}
					/>
					<TextInput
						{...form.getInputProps('slug')}
						label="Slug"
						description="This will appear in the browser URL."
						disabled={isLoading}
						withAsterisk
					/>
					<Select
						{...form.getInputProps('categoryId')}
						data={categoryData}
						label="Initial category"
						placeholder={isLoadingCategories ? 'Loading...' : '--- Please select ---'}
						nothingFoundMessage="No search results"
						disabled={isLoading || isLoadingCategories || categoryId !== undefined}
						searchable
					/>
					<Group justify="flex-end">
						<Button type="button" variant="subtle" onClick={handleCancel} disabled={isLoading}>
							Cancel
						</Button>
						<Button type="submit" disabled={isLoading}>
							Add event {isLoading && <Loader size="xs" ml="xs" />}
						</Button>
					</Group>
				</Stack>
			</form>
		</Container>
	);
}

export type AddEventForm = Pick<EventSingleFragment, 'title' | 'slug' | 'description'> & {
	categoryId: string;
};
